/**
 * Dashboard einer Abteilung für Mitglieder: reine Anzeige.
 * Kein Bearbeiten, kein Voll/Kompakt, kein Zurücksetzen, das Layout legt der Admin fest.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionTitle } from '../../components/ui';
import { departmentsApi, type DepartmentDashboard } from '../../lib/departments';
import { getThemeColors } from '../../lib/theme';
import { DashboardCanvas, hasDepartmentDashboard } from '../dashboard/AdminDashboardPage/DashboardCanvas';
import type { Density } from '../../types';

interface Props {
  slug: string;
  dark: boolean;
  density: Density;
  setPage: (page: string) => void;
}

export function AbteilungDashboardView({ slug, dark, density, setPage }: Props) {
  const { t: tr } = useTranslation();
  const t = getThemeColors(dark);
  const [state, setState] = useState<{ loading: boolean; error: boolean; value: DepartmentDashboard | null }>({
    loading: true, error: false, value: null,
  });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, error: false, value: null });
    departmentsApi.dashboardBySlug(slug)
      .then(value => { if (alive) setState({ loading: false, error: false, value }); })
      .catch(() => { if (alive) setState({ loading: false, error: true, value: null }); });
    return () => { alive = false; };
  }, [slug]);

  if (state.loading) {
    return <div style={{ padding: 24, color: t.muted, fontSize: 13 }}>{tr('departments.page.loading')}</div>;
  }
  if (state.error || !state.value) {
    return (
      <Card dark={dark} style={{ padding: 24, maxWidth: 520 }}>
        <div style={{ color: '#b42318', fontSize: 13 }}>{tr('departments.dashboard.load_error')}</div>
      </Card>
    );
  }

  const config = state.value.config ?? {};
  if (!hasDepartmentDashboard(config)) {
    return (
      <Card dark={dark} style={{ padding: 24, maxWidth: 560 }} >
        <div data-testid="dept-dashboard-empty">
          <SectionTitle
            dark={dark}
            title={tr('departments.dashboard.empty_title')}
            subtitle={tr('departments.dashboard.empty_text')}
          />
        </div>
      </Card>
    );
  }

  return <DashboardCanvas dark={dark} density={density} config={config} setPage={setPage} editable={false} />;
}
